import { ref, reactive } from 'vue'
import Swal from 'sweetalert2'
import type { Attachment } from '@/types/chat'

export interface UseAudioRecorderOptions {
  /** Quantidade de barras do waveform (varia com a largura de cada UI). Padrão 22. */
  barCount?: number
  /** Se true, pausa qualquer outro áudio em reprodução ao tocar um novo. Padrão false. */
  exclusivePlayback?: boolean
  /** Chamado com o arquivo gravado ao confirmar o envio (cada componente decide como enviar). */
  onSend: (file: File) => Promise<void>
}

/** Player de áudio de anexos + gravação de áudio pelo microfone. */
export function useAudioRecorder(options: UseAudioRecorderOptions) {
  const barCount = options.barCount ?? 22
  const exclusivePlayback = options.exclusivePlayback ?? false

  /* ── Player ── */
  const audioState = reactive<Record<number, { playing: boolean; current: number; duration: number }>>({})
  const audioEls: Record<number, HTMLAudioElement> = {}

  function ensureAudio(att: Attachment): HTMLAudioElement {
    if (!audioEls[att.id]) {
      const el = new Audio(att.data_url)
      audioState[att.id] = { playing: false, current: 0, duration: 0 }
      el.addEventListener('timeupdate', () => { audioState[att.id].current = el.currentTime })
      el.addEventListener('loadedmetadata', () => { audioState[att.id].duration = el.duration })
      el.addEventListener('ended', () => {
        audioState[att.id].playing = false
        audioState[att.id].current = 0
        el.currentTime = 0
      })
      audioEls[att.id] = el
    }
    return audioEls[att.id]
  }

  function toggleAudio(att: Attachment) {
    const el = ensureAudio(att)
    if (audioState[att.id]?.playing) {
      el.pause()
      audioState[att.id].playing = false
    } else {
      if (exclusivePlayback) {
        Object.keys(audioEls).forEach(id => {
          const nid = Number(id)
          if (nid !== att.id && !audioEls[nid].paused) {
            audioEls[nid].pause()
            if (audioState[nid]) audioState[nid].playing = false
          }
        })
      }
      el.play().catch(() => { audioState[att.id].playing = false })
      audioState[att.id].playing = true
    }
  }

  function seekAudio(att: Attachment, e: Event) {
    const el = ensureAudio(att)
    const pct = Number((e.target as HTMLInputElement).value)
    if (el.duration) el.currentTime = (pct / 100) * el.duration
  }

  function audioProgress(id: number): number {
    const s = audioState[id]
    return s?.duration ? (s.current / s.duration) * 100 : 0
  }

  function fmtTime(secs: number): string {
    if (!secs || isNaN(secs)) return '0:00'
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  function disposeAudioEls() {
    Object.values(audioEls).forEach(el => { el.pause(); el.src = '' })
  }

  /* ── Gravação ── */
  const recording     = ref(false)
  const recordingTime = ref(0)
  const recordedBlob  = ref<Blob | null>(null)
  const recordedUrl   = ref('')
  const waveformBars  = ref<number[]>(new Array(barCount).fill(3))
  let _mr: MediaRecorder | null = null
  let _stream: MediaStream | null = null
  let _recTimer: ReturnType<typeof setInterval> | null = null
  let _animFrame: number | null = null

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      _stream = stream
      const chunks: Blob[] = []
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm'

      const actx = new AudioContext()
      const src  = actx.createMediaStreamSource(stream)
      const anal = actx.createAnalyser()
      anal.fftSize = 64
      src.connect(anal)
      const buf = new Uint8Array(anal.frequencyBinCount)

      const drawFrame = () => {
        anal.getByteFrequencyData(buf)
        waveformBars.value = Array.from({ length: barCount }, (_, i) => {
          const idx = Math.floor(i * buf.length / barCount)
          return Math.max(3, Math.round(buf[idx] / 255 * barCount))
        })
        _animFrame = requestAnimationFrame(drawFrame)
      }
      drawFrame()

      _mr = new MediaRecorder(stream, { mimeType })
      _mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
      _mr.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType })
        recordedBlob.value = blob
        if (recordedUrl.value) URL.revokeObjectURL(recordedUrl.value)
        recordedUrl.value = URL.createObjectURL(blob)
        stream.getTracks().forEach(t => t.stop())
        actx.close()
      }
      _mr.start(100)
      recording.value = true
      recordingTime.value = 0
      _recTimer = setInterval(() => recordingTime.value++, 1000)
    } catch {
      Swal.fire({ title: 'Microfone inacessível', text: 'Não foi possível acessar o microfone. Verifique as permissões do navegador.', icon: 'error' })
    }
  }

  function stopRecording() {
    _mr?.stop()
    recording.value = false
    if (_recTimer) { clearInterval(_recTimer); _recTimer = null }
    if (_animFrame) { cancelAnimationFrame(_animFrame); _animFrame = null }
    waveformBars.value = new Array(barCount).fill(3)
  }

  function cancelRecording() {
    if (recording.value) {
      _mr?.stop()
      _stream?.getTracks().forEach(t => t.stop())
      recording.value = false
    }
    if (_recTimer) { clearInterval(_recTimer); _recTimer = null }
    if (_animFrame) { cancelAnimationFrame(_animFrame); _animFrame = null }
    recordedBlob.value = null
    if (recordedUrl.value) { URL.revokeObjectURL(recordedUrl.value); recordedUrl.value = '' }
    waveformBars.value = new Array(barCount).fill(3)
  }

  async function sendRecordedAudio() {
    if (!recordedBlob.value) return
    const ext  = recordedBlob.value.type.includes('mp4') ? 'm4a' : 'webm'
    const file = new File([recordedBlob.value], `audio_${Date.now()}.${ext}`, { type: recordedBlob.value.type })
    recordedBlob.value = null
    if (recordedUrl.value) { URL.revokeObjectURL(recordedUrl.value); recordedUrl.value = '' }
    await options.onSend(file)
  }

  function fmtRecordingTime(secs: number): string {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return {
    audioState, ensureAudio, toggleAudio, seekAudio, audioProgress, fmtTime, disposeAudioEls,
    recording, recordingTime, recordedBlob, recordedUrl, waveformBars,
    startRecording, stopRecording, cancelRecording, sendRecordedAudio, fmtRecordingTime,
  }
}
